// "My Day" home (30-day list row 19). Every open project on one screen: what is yours and
// when it is due, what is waiting on someone else, what needs a decision, and (office) what the
// analytics rollup flagged. Nobody has to open a project to find their own work.
import { useState } from 'react';
import { Link } from 'react-router';
import { visibleModules } from '@tradeview/core';
import { useAuth } from '../auth';
import { Icon } from '../app/Icon';
import { ENV } from '../env';
import { useMyDay, KIND_LABEL, KIND_ICON, type DayRow, type MyDay } from '../app/myday';
import { useAlerts, unreadCount } from '../app/alerts';
import { fmtDate } from '../app/fmt';

export function Home() {
  const { session } = useAuth();
  const mods = visibleModules(session?.role ?? null, session?.entitlements ?? null);
  const first = (session?.displayName ?? '').split(' ')[0] || 'there';
  const today = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date());
  const day = useMyDay();
  const unread = unreadCount(useAlerts());
  const d = day.data;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="pt-1.5 pb-1 flex items-start justify-between gap-2">
        <div>
          <h2 className="font-disp font-bold text-[30px] leading-none m-0">Good {partOfDay()}, {first}</h2>
          <p className="m-0 text-muted text-[13.5px]">{today}{d ? ` · ${d.projects} open project${d.projects === 1 ? '' : 's'}` : ''}{ENV.name !== 'production' ? ` · ${ENV.name}` : ''}</p>
        </div>
        {unread > 0 && <Link to="/alerts" className="btn btn-sm shrink-0"><Icon name="bell" size={16} />{unread} new</Link>}
      </div>

      {day.isLoading && <><div className="skel h-24 rounded-[14px]" /><div className="skel h-40 rounded-[14px]" /></>}
      {day.isError && <div className="card flex items-center gap-2.5"><Icon name="warn" size={18} className="text-crit shrink-0" /><span className="flex-1 text-[13.5px]">Couldn't load your day. {day.error instanceof Error ? day.error.message : ''}</span><button className="btn btn-sm" onClick={() => day.refetch()}>Try again</button></div>}

      {d && <Yours d={d} />}
      {d && d.decide.length > 0 && <Rows title="Needs a decision" rows={d.decide} tone="decide" today={d.today} />}
      {d && d.office && d.flags.length > 0 && <Flags flags={d.flags} />}
      {d && d.waiting.length > 0 && <Rows title="Waiting on others" rows={d.waiting} tone="waiting" today={d.today} />}

      {mods.length === 0 ? (
        <div className="card text-center py-6">
          <div className="ic ic-acc mx-auto mb-2.5 w-12 h-12 rounded-[12px]"><Icon name="grid" size={24} /></div>
          <b className="font-head font-bold text-base block">No modules on yet</b>
          <p className="text-muted text-[13.5px] m-0 mb-3">When the company turns a module on, its card shows here.</p>
          <Link className="btn btn-primary" to="/modules">See modules</Link>
        </div>
      ) : (
        <section>
          <h4 className="lbl m-0 mb-1.5 mt-1">Open</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {mods.map(m => <Link key={m.manifest.id} to={m.manifest.routes[0].path} className="card py-2.5 px-3 flex items-center gap-2 min-w-0"><Icon name={m.manifest.nav[0]?.icon || 'grid'} size={18} className="text-muted shrink-0" /><span className="min-w-0 flex-1"><b className="font-head font-semibold text-[13.5px] block truncate">{m.manifest.name}</b><span className="text-muted text-[11.5px] block truncate">{m.manifest.marketing.tagline}</span></span></Link>)}
          </div>
        </section>
      )}
    </div>
  );
}

/** Needs you (office): the analytics rollup's flags, the worst first. */
function Flags({ flags }: { flags: MyDay['flags'] }) {
  const [all, setAll] = useState(false);
  const shown = all || flags.length <= 5 ? flags : flags.slice(0, 5);
  return (
    <section className="card py-1">
      <h4 className="lbl m-0 mt-1.5 mb-1">Needs you · {flags.length}</h4>
      {shown.map((f, i) => <Link key={i} to={f.link} className="flex items-center gap-2.5 py-2 border-t border-line first:border-0"><Icon name="flag" size={16} className="text-warn shrink-0" /><span className="min-w-0 flex-1"><b className="font-semibold text-[13.5px] block truncate">{f.label}</b><span className="text-muted text-[12.5px] block truncate">{f.text}</span></span><Icon name="chevron" size={16} className="text-muted shrink-0" /></Link>)}
      {shown.length < flags.length && <button className="text-accent text-[13px] font-head font-semibold py-2" onClick={() => setAll(true)}>Show all {flags.length}</button>}
    </section>
  );
}

/** Yours: late, today, this week, later, no date. */
function Yours({ d }: { d: MyDay }) {
  const late = d.mine.filter(r => r.days != null && r.days < 0);
  const todayRows = d.mine.filter(r => r.days === 0);
  const week = d.mine.filter(r => r.days != null && r.days > 0 && r.due! <= d.weekEnd);
  const later = d.mine.filter(r => r.days != null && r.due! > d.weekEnd);
  const undated = d.mine.filter(r => r.days == null);
  if (d.mine.length === 0) return (
    <section className="card text-center py-5">
      <div className="ic ic-acc mx-auto mb-2 w-11 h-11 rounded-[12px] border-0"><Icon name="check" size={22} /></div>
      <b className="font-head font-bold text-[15px] block">Nothing on you across {d.projects} project{d.projects === 1 ? '' : 's'}</b>
      <p className="text-muted text-[13px] m-0">Plan rows, RFIs, submittals, change orders, punch items and requests land here the moment someone puts your name on them.</p>
    </section>
  );
  return (
    <section className="card py-1">
      <h4 className="lbl m-0 mt-1.5 mb-1 flex items-center justify-between">Yours · {d.mine.length}{late.length > 0 && <span className="pill pill-nodot pill-crit">{late.length} late</span>}</h4>
      <Group label="Late" rows={late} today={d.today} tone="mine" />
      <Group label="Today" rows={todayRows} today={d.today} tone="mine" />
      <Group label="This week" rows={week} today={d.today} tone="mine" />
      <Group label="Later" rows={later} today={d.today} tone="mine" />
      <Group label="No date" rows={undated} today={d.today} tone="mine" />
    </section>
  );
}
function Group({ label, rows, today, tone }: { label: string; rows: DayRow[]; today: string; tone: Tone }) {
  if (!rows.length) return null;
  return <>
    <div className="text-[11px] font-head font-bold uppercase tracking-wide text-muted pt-2 pb-0.5">{label}</div>
    {rows.map(r => <Row key={`${r.kind}:${r.pid}:${r.id}`} r={r} today={today} tone={tone} />)}
  </>;
}
type Tone = 'mine' | 'waiting' | 'decide';
function Rows({ title, rows, tone, today }: { title: string; rows: DayRow[]; tone: Tone; today: string }) {
  const [all, setAll] = useState(false);
  const shown = all || rows.length <= 8 ? rows : rows.slice(0, 8);
  return (
    <section className="card py-1">
      <h4 className="lbl m-0 mt-1.5 mb-1">{title} · {rows.length}</h4>
      {shown.map(r => <Row key={`${r.kind}:${r.pid}:${r.id}`} r={r} today={today} tone={tone} />)}
      {shown.length < rows.length && <button className="text-accent text-[13px] font-head font-semibold py-2" onClick={() => setAll(true)}>Show all {rows.length}</button>}
    </section>
  );
}
function Row({ r, tone }: { r: DayRow; today: string; tone: Tone }) {
  const due = r.days == null ? null : r.days < 0 ? { text: `${-r.days} day${r.days === -1 ? '' : 's'} late`, cls: 'pill-crit' } : r.days === 0 ? { text: 'Today', cls: 'pill-warn' } : r.days === 1 ? { text: 'Tomorrow', cls: 'pill-warn' } : { text: fmtDate(r.due, 'weekday'), cls: 'pill-info' };
  const waited = tone === 'waiting' && r.waited != null ? { text: r.waited === 0 ? 'today' : `${r.waited} day${r.waited === 1 ? '' : 's'}`, cls: r.waited > 7 ? 'pill-warn' : 'pill-nodot' } : null;
  return (
    <Link to={r.href} className="flex items-center gap-2.5 py-2 border-t border-line first:border-0 min-w-0">
      <span className="ic w-8 h-8 rounded-[9px] shrink-0 grid place-items-center" title={KIND_LABEL[r.kind] || r.kind}><Icon name={KIND_ICON[r.kind] || 'grid'} size={16} /></span>
      <span className="min-w-0 flex-1">
        <b className="font-semibold text-[13.5px] block truncate">{r.label}</b>
        <span className="text-muted text-[12.5px] block truncate">{[KIND_LABEL[r.kind] || null, r.project ? (r.bid ? `Bid · ${r.project}` : r.project) : null, tone === 'decide' && r.who ? `from ${r.who}` : null, r.text].filter(Boolean).join(' · ')}</span>
      </span>
      {waited ? <span className={`pill ${waited.cls} shrink-0`}>{waited.text}</span> : due ? <span className={`pill pill-nodot ${due.cls} shrink-0`}>{due.text}</span> : null}
    </Link>
  );
}

function partOfDay() { const h = new Date().getHours(); return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'; }
